import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  ElementRef,
  OnDestroy,
  EventEmitter,
  Output,
  AfterViewInit,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Message, PosterType } from 'src/app/models/user';
import { ChatGptService, PAGE_COUNT } from 'src/app/services/chat-gpt.service';
import { UserService } from 'src/app/services/user.service';
import { TruncateStringPipe } from 'src/app/standalones/pipes/truncate-string.pipe';
import { AppIcon, AppIconSource } from 'src/app/services/items-canvas.service';
import { ItemModel } from 'src/app/models/itemModel';
import { ScreenshotService } from '../../../services/screenshot.service';
import { INTERACTIONS_BAR_HEIGHT, INTERACTIONS_BAR_PADDING } from '../iframe/additional-apps.utils';
import { AppsInteractionsLockService } from '../item-wrapper/apps-interactions-lock.service';
import { BoardChatGptConfigurations } from '../items-canvas/board-items/board-chat-gpt-configurations';
import { GPTAppsService } from '../../../services/space-apps.service';
import { BoardItemsInteractionsManager } from '../items-canvas/board-items-interactions-manager';
import { SpaceRepository } from '../../../state/space.repository';

@UntilDestroy()
@Component({
  selector: 'app-chat-gpt[state][static]',
  templateUrl: './chat-gpt.component.html',
  styleUrls: ['./chat-gpt.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatGptComponent implements OnDestroy, AfterViewInit, OnChanges {
  @Input() state!: BoardChatGptConfigurations;
  @Input() static!: boolean;
  @Output() stateChanged = new EventEmitter<BoardChatGptConfigurations>();
  @Output() deleteItem = new EventEmitter<void>();

  @ViewChild('chatGPTContainer') chatGPTContainer!: ElementRef<HTMLDivElement>;

  private _currentPageSubject = new BehaviorSubject<number>(1);
  currentPage$ = this._currentPageSubject.asObservable();
  readonly shouldDisableInteractions$ = new BehaviorSubject<boolean>(false);
  readonly selected$ = new BehaviorSubject<boolean>(false);
  readonly ItemModel = ItemModel;

  infiniteScrollDisabled = true;

  private _data: Message[] = [];
  private _messageIds = new Set<string>();
  data$: Observable<Message[]> = of([]);
  botWriting$: Observable<boolean> = of(false);
  placeholder = '';
  typingMessage = '';
  tooltipDisabled$ = this.botWriting$.pipe(map((value) => !value));
  readonly appIcon$ = new BehaviorSubject<AppIcon>({
    type: AppIconSource.ICON,
    content: '',
    height: 0,
    width: 0,
  });

  readonly formattedTitle$ = new BehaviorSubject<string>('');

  constructor(
    private gptService: ChatGptService,
    private userService: UserService,
    private truncate: TruncateStringPipe,
    private screenshotService: ScreenshotService,
    private appsInteractionsLockService: AppsInteractionsLockService,
    private gptAppsService: GPTAppsService,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    protected spaceRepo: SpaceRepository,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    if (!this.static) {
      this.initObservables();
      this.setUpInteractionsLock();
      this.setUpSelectionSubscription();
      this.setupTitle();
    }

    this.setupIcon();

    this.appsInteractionsLockService.onInteractionsLockChanged(
      this.state.interactionsLocked,
      this.state.uid,
    );
  }

  ngOnChanges(changes) {
    if (!changes.state?.previousValue || !changes.state?.currentValue) {
      return;
    }

    const previousConfig = changes.state.previousValue as BoardChatGptConfigurations;
    const currentConfig = changes.state.currentValue as BoardChatGptConfigurations;

    if (previousConfig.interactionsLocked !== currentConfig.interactionsLocked) {
      this.appsInteractionsLockService.onInteractionsLockChanged(
        this.state.interactionsLocked,
        this.state.uid,
      );
    }
  }

  private setUpInteractionsLock() {
    if (!this.state) {
      return;
    }
    this.appsInteractionsLockService
      .interactionsLockState$(this.state.uid, this.state.interactionsLocked)
      .pipe(untilDestroyed(this))
      .subscribe((interactionsLockState) =>
        this.shouldDisableInteractions$.next(interactionsLockState.interactionsDisabledForUser),
      );
  }

  private setUpSelectionSubscription() {
    this.boardItemsInteractionsManager
      .itemSelected$(this.state.uid)
      .pipe(untilDestroyed(this))
      .subscribe((selected) => {
        this.selected$.next(selected);
        this.cdr.detectChanges();
      });
  }

  initObservables(): void {
    this.data$ = combineLatest([
      this.currentPage$.pipe(
        distinctUntilChanged(),
        tap(() => (this._data = [])),
        switchMap((page) =>
          this.gptService.getAppConversation$(this.state.chatId, page, PAGE_COUNT),
        ),
      ),
      this.gptService.getMessageUpdates$(this.state.chatId),
    ]).pipe(
      tap(() => this.setupTitle()),
      map(([{ messages, total_messages }, update]) => {
        this.infiniteScrollDisabled =
          total_messages <= this._currentPageSubject.getValue() * PAGE_COUNT;

        if (!this._data.length) {
          this._data = messages;
        }

        if (update && !this._messageIds.has(update._id)) {
          this._messageIds.add(update._id);
          this._data.push(update);
        }

        return this._data;
      }),
      map((data) =>
        data.sort(
          (prev, curr) => new Date(curr.updatedAt).getTime() - new Date(prev.updatedAt).getTime(),
        ),
      ),
    );

    this.botWriting$ = this.data$.pipe(
      map((messages) => messages.length > 0 && messages[0].posterType === PosterType.User),
    );

    this.placeholder = `Ask ${this.state.appConfig.name} something...`;
    this.typingMessage = `${this.state.appConfig.name} IS TYPING...`.toUpperCase();
  }

  gotConversationTitle = false;
  setupTitle(): void {
    if (this.gotConversationTitle) {
      return;
    }
    this.gptAppsService
      .getApp(this.state.chatId)
      .pipe(take(1))
      .subscribe((app) => {
        this.gotConversationTitle = app.title !== 'New chat';
        const appName = app.name || this.state.appConfig.name || 'Chat with GPT';
        const appTitle = this.truncate.transform(app.title || 'Chat with GPT', 45);
        this.formattedTitle$.next(`
      <span class="heading-app-name">${appName}</span>
      <span class="dot-app-separator">•</span>
      <span class="heading-app-title">${appTitle}</span>
    `);
      });
  }

  setupIcon() {
    const icon = this.state.appConfig.icon ?? '';
    this.appIcon$.next({
      type: AppIconSource.HTTP_IMG,
      content: icon,
      height: 32,
      width: 32,
    });
  }

  onInteractionsLockChanges(interactionsLocked: boolean): void {
    this.state.interactionsLocked = interactionsLocked;
    this.appsInteractionsLockService.onInteractionsLockChanged(interactionsLocked, this.state.uid);
    this.emitChanges();
  }

  onPrivacyChanged(isPrivate: boolean): void {
    this.state.isPrivate = isPrivate;
    this.emitChanges();
  }

  emitChanges(): void {
    this.stateChanged.emit(this.state);
  }

  question: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(1),
  ]);
  inProgress = false;

  handleSendMessage(event: KeyboardEvent): void {
    if (this.shouldDisableInteractions$.getValue()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (event.shiftKey || event.ctrlKey) {
      return;
    }

    const question: string = this.question.value;
    if (this.question.invalid || this.inProgress || !question.trim() || this.botWriting) {
      return;
    }
    this.inProgress = true;
    this.question.setValue('', { onlySelf: true });

    this.gptService
      .askQuestion({
        appId: this.state.chatId,
        question,
      })
      .pipe(take(1))
      .subscribe({
        complete: () => {
          this.inProgress = false;
        },
        error: () => {
          this.question.setValue(question);
        },
      });
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key.toLowerCase() === 'a' && (event.ctrlKey || event.metaKey)) {
      (event.target as HTMLTextAreaElement).select();
    }
  }

  handleScrollUp(): void {
    this._currentPageSubject.next(this._currentPageSubject.value + 1);
  }

  get botWriting(): boolean {
    return this._data.length > 0 && this._data[0].posterType === PosterType.User;
  }

  isCurrentUser(userId: string): boolean {
    return this.userService.userId === userId;
  }

  trackMessage(index: number, message: { _id: string }): string {
    return message._id;
  }

  async takeScreenshot(): Promise<void> {
    const component = this.chatGPTContainer.nativeElement;

    const { componentTop, componentLeft, componentWidth, componentHeight } = {
      componentTop: this.state.top,
      componentLeft: this.state.left,
      componentWidth: this.state.width,
      componentHeight: this.state.height,
    };
    const screenshotBoundingRect = {
      top:
        componentTop +
        INTERACTIONS_BAR_HEIGHT +
        INTERACTIONS_BAR_PADDING +
        (componentHeight - INTERACTIONS_BAR_HEIGHT) / 2,
      left: componentLeft + componentWidth + componentWidth / 2,
      width: componentWidth,
      height: componentHeight - INTERACTIONS_BAR_HEIGHT,
    };

    // getting the content from the user perspective
    const screenshotContainer = document.getElementById('screenshot-invisible-container');
    if (!screenshotContainer) {
      return;
    }

    screenshotContainer.style['height'] = `${component.offsetHeight}px`;
    screenshotContainer.style['width'] = `${component.offsetWidth}px`;

    // removing the transformation by cloning
    const clonedElement = component.cloneNode(true);
    screenshotContainer.appendChild(clonedElement);

    // scrolling in the cloned conversation to the position needed
    if (screenshotContainer.children[0].children[0]) {
      screenshotContainer.children[0].children[0].scrollTop = component.children[0].scrollTop;
    }

    await this.screenshotService.takeScreenshot(
      screenshotContainer as HTMLDivElement,
      componentWidth,
      componentHeight,
      screenshotBoundingRect,
    );

    // to avoid having the old screenshot content above the next screenshot content
    screenshotContainer.innerHTML = '';
  }

  ngOnDestroy(): void {
    this.gptService.unsubscribeFromUpdates(this.state.chatId);
  }
}
